/**
 * Staff-only raw BLE diagnostic console for OutdoorLink on-site hardware.
 *
 * Lets staff scan for BLE devices, connect to one, and send/receive raw UART
 * bytes over the OutdoorLink GATT service — useful for verifying firmware
 * communication without going through the provisioning flow. Access is
 * staff-gated at the route level.
 *
 * Contains:
 * - BluetoothDebugView — the diagnostic component (default export)
 */

import { BleClient } from '@capacitor-community/bluetooth-le';
import { Button, List, ListItem, ListItemText, Alert, CircularProgress, Typography, Stack, Box } from '@mui/material';
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { UART_SERVICE_UUID, UART_TX_CHAR, UART_RX_CHAR } from '@sophocles/features/bluetooth/connectionService';

export default function BluetoothDebugView() {
  const { t } = useTranslation();
  const [initialized, setInitialized] = useState(false);
  const [initError, setInitError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [connectedDevice, setConnectedDevice] = useState(null);
  const [consoleOutput, setConsoleOutput] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [writeError, setWriteError] = useState(null);
  const [deviceServices, setDeviceServices] = useState({}); // deviceId -> array of {service, characteristics}
  const subscriptionRef = useRef(null);
  const initializedRef = useRef(false);

  /** Initialize BleClient on first scan attempt. No-op after first success. */
  const ensureInitialized = async () => {
    if (initializedRef.current) return true;
    try {
      await BleClient.initialize();
      initializedRef.current = true;
      setInitialized(true);
      return true;
    } catch (err) {
      setInitError(t('bluetoothDebug.bleInitFailed', 'BLE init failed: {{error}}', { error: err?.message || err }));
      return false;
    }
  };

  // Helper to discover and list all services/characteristics for a device
  const fetchDeviceServices = async deviceId => {
    try {
      const services = await BleClient.getServices(deviceId);
      setDeviceServices(prev => ({ ...prev, [deviceId]: services }));
    } catch (err) {
      setDeviceServices(prev => ({ ...prev, [deviceId]: { error: err?.message || String(err) } }));
    }
  };

  const handleScan = async () => {
    setDevices([]);
    setScanError(null);
    setScanning(true);
    setDeviceServices({});

    const ready = await ensureInitialized();
    if (!ready) {
      setScanning(false);
      return;
    }

    try {
      await BleClient.requestLEScan(
        {
          /*services: [UART_SERVICE_UUID]*/
        },
        async result => {
          console.log('Discovered device:', result);
          setDevices(prev => {
            if (prev.some(d => d.device.deviceId === result.device.deviceId)) return prev;
            // Fetch services for this device (non-blocking)
            fetchDeviceServices(result.device.deviceId);
            return [...prev, result];
          });
        },
      );
      setTimeout(async () => {
        await BleClient.stopLEScan();
        setScanning(false);
      }, 10000);
    } catch (err) {
      setScanError(
        t('bluetoothDebug.scanFailed', 'Failed to scan for devices: {{error}}', { error: err?.message || err }),
      );
      setScanning(false);
    }
  };

  const handleConnect = async device => {
    setConnecting(true);
    setWriteError(null);
    setConsoleOutput('');
    try {
      await BleClient.connect(device.deviceId);
      setConnectedDevice(device);
      // Subscribe to RX notifications
      subscriptionRef.current = await BleClient.startNotifications(
        device.deviceId,
        UART_SERVICE_UUID,
        UART_RX_CHAR,
        value => {
          const decoder = new TextDecoder('utf-8');
          setConsoleOutput(prev => prev + decoder.decode(value));
        },
      );
    } catch (err) {
      setWriteError(t('bluetoothDebug.connectFailed', 'Failed to connect: {{error}}', { error: err?.message || err }));
    }
    setConnecting(false);
  };

  const handleDisconnect = async () => {
    if (connectedDevice) {
      try {
        await BleClient.disconnect(connectedDevice.deviceId);
      } catch (e) {
        // ignore
      }
      setConnectedDevice(null);
      setConsoleOutput('');
      setInputValue('');
      setWriteError(null);
    }
  };

  const handleSend = async e => {
    e.preventDefault();
    setWriteError(null);
    if (!connectedDevice || !inputValue) return;
    try {
      const encoder = new TextEncoder();
      await BleClient.writeWithoutResponse(
        connectedDevice.deviceId,
        UART_SERVICE_UUID,
        UART_TX_CHAR,
        encoder.encode(inputValue),
      );
      setInputValue('');
    } catch (err) {
      setWriteError(t('bluetoothDebug.sendFailed', 'Failed to send: {{error}}', { error: err?.message || err }));
    }
  };

  return (
    <Stack spacing={2} sx={{ maxWidth: 600, margin: '0 auto', mt: 4 }}>
      <Typography variant="h5">{t('bluetoothDebug.title', 'Bluetooth Debug View')}</Typography>
      {initError && <Alert severity="error">{initError}</Alert>}
      {scanError && <Alert severity="error">{scanError}</Alert>}
      {writeError && <Alert severity="error">{writeError}</Alert>}
      {scanning && !initialized && <CircularProgress />}
      {!connectedDevice && (
        <Button variant="contained" onClick={handleScan} disabled={scanning}>
          {scanning
            ? t('bluetoothDebug.scanning', 'Scanning...')
            : t('bluetoothDebug.scanForDevices', 'Scan for Devices')}
        </Button>
      )}
      {!connectedDevice && (
        <List>
          {devices.map(({ device }) => (
            <ListItem
              key={device.deviceId}
              divider
              secondaryAction={
                <Button onClick={() => handleConnect(device)} disabled={connecting}>
                  {connecting ? t('bluetoothDebug.connecting', 'Connecting...') : t('bluetoothDebug.pair', 'Pair')}
                </Button>
              }
            >
              <ListItemText
                primary={device.name || t('bluetoothDebug.unnamedDevice', 'Unnamed Device')}
                secondary={device.deviceId}
              />
              {/* Show discovered services/characteristics for this device */}
              {deviceServices[device.deviceId] && (
                <Box sx={{ mt: 1, ml: 2, bgcolor: 'var(--surface-secondary)', borderRadius: 'var(--radius-md)', p: 1 }}>
                  {deviceServices[device.deviceId].error ? (
                    <Typography color="error" variant="caption">
                      {t('bluetoothDebug.errorLabel', 'Error: {{message}}', {
                        message: deviceServices[device.deviceId].error,
                      })}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        {t('bluetoothDebug.servicesAndCharacteristics', 'Services & Characteristics:')}
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {deviceServices[device.deviceId].map(service => (
                          <li key={service.uuid}>
                            <span style={{ fontWeight: 'bold' }}>{service.uuid}</span>
                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                              {service.characteristics.map(char => (
                                <li key={char.uuid}>
                                  {char.uuid}
                                  {char.properties && (
                                    <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>
                                      [
                                      {Object.keys(char.properties)
                                        .filter(p => char.properties[p])
                                        .join(', ')}
                                      ]
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </Box>
              )}
            </ListItem>
          ))}
        </List>
      )}
      {initialized && !scanning && devices.length === 0 && !connectedDevice && (
        <Typography variant="body2" color="text.secondary">
          {t('bluetoothDebug.noDevicesFound', 'No devices found.')}
        </Typography>
      )}
      {connectedDevice && (
        <>
          <Alert severity="success">
            {t('bluetoothDebug.connectedTo', 'Connected to {{name}}', {
              name: connectedDevice.name || connectedDevice.deviceId,
            })}
          </Alert>
          <Button variant="outlined" onClick={handleDisconnect} sx={{ mb: 1 }}>
            {t('bluetoothDebug.disconnect', 'Disconnect')}
          </Button>
          <Typography variant="subtitle1">
            {t('bluetoothDebug.serialConsoleOutput', 'Serial Console Output')}
          </Typography>
          <Box
            sx={{
              bgcolor: 'var(--surface-page)',
              color: 'success.main',
              p: 2,
              borderRadius: 'var(--radius-md)',
              minHeight: 120,
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              maxHeight: 300,
              overflowY: 'auto',
              border: '1px solid var(--border-default)',
            }}
          >
            {consoleOutput || (
              <span style={{ color: 'var(--text-muted)' }}>{t('bluetoothDebug.noOutputYet', '[No output yet]')}</span>
            )}
          </Box>
          <form onSubmit={handleSend} style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              placeholder={t('bluetoothDebug.typeCommand', 'Type command...')}
              style={{
                flex: 1,
                fontFamily: 'monospace',
                fontSize: 16,
                padding: 8,
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)',
              }}
              autoFocus
            />
            <Button type="submit" variant="contained" disabled={!inputValue}>
              {t('bluetoothDebug.send', 'Send')}
            </Button>
          </form>
        </>
      )}
    </Stack>
  );
}
